/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import { withMap } from 'react-mapbox-gl/lib-esm/context';
import cn from 'classnames';
import Cookies from 'js-cookie';
import { i18n } from '@lt/localization';

import LayersControlDemo from './LayersControlDemo';
import LayerControlButton from './LayerControlButton';
import LayersControlMessage from './LayersControlMessage';

const DEMO_COOKIE = {
  name: 'hide_map_controls_demo',
  expires: 2 * 7 * 24 * 60 * 60, // 2 weeks in seconds
};
// Show demo this much times
const DEMO_SHOW_TIMES = 2;
// Minimum level of zoom to see the extra layers
const LAYERS_MIN_ZOOM = 11;

const baseCls = 'lt-map-layers-controls';

class LayersControl extends React.PureComponent {
  static getCookieValue() {
    try {
      const demoCookie = Cookies.get(DEMO_COOKIE.name);
      return demoCookie ? parseInt(demoCookie, 10) : 0;
    } catch (error) {
      return null;
    }
  }

  constructor(props) {
    super(props);

    const showDemo = props.disableDemo
      ? false
      : LayersControl.getCookieValue() < DEMO_SHOW_TIMES;

    this.hideDemo = this.hideDemo.bind(this);
    this.handleButtonClicked = this.handleButtonClicked.bind(this);
    this.state = { showDemo, warnMsg: null };
  }

  handleButtonClicked() {
    const { showDemo } = this.state;
    if (showDemo) {
      this.hideDemo();
    }

    if (this.zoom < LAYERS_MIN_ZOOM) {
      this.showWarning(i18n.t('map.controls.warning'));
    }
  }

  get zoom() {
    const { map } = this.props;
    return map && map.getZoom();
  }

  showWarning(warnMsg, duration) {
    this.setState({ warnMsg });
    clearTimeout(this.timeout);
    this.timeout = setTimeout(
      () => this.setState({ warnMsg: null }),
      duration || 2500,
    );
  }

  hideDemo(dontShowAgain) {
    const timesShown = dontShowAgain
      ? DEMO_SHOW_TIMES
      : LayersControl.getCookieValue() + 1;
    const { name, expires } = DEMO_COOKIE;
    Cookies.set(name, timesShown, { expires });
    this.setState({ showDemo: false });
  }

  render() {
    const { layers, style, disabled } = this.props;
    const { showDemo, warnMsg } = this.state;
    const className = cn(baseCls, { [`${baseCls}--disabled`]: disabled });
    return (
      <div className={className} style={style}>
        {showDemo && !disabled && (
          <LayersControlDemo onClose={() => this.hideDemo(true)} />
        )}
        {warnMsg && !disabled && (
          <LayersControlMessage>{warnMsg}</LayersControlMessage>
        )}
        <div className={`${baseCls}__buttons`}>
          {layers.map((layer) => (
            <LayerControlButton
              key={layer.id}
              onClick={this.handleButtonClicked}
              disabled={disabled}
              className={`${baseCls}__button`}
              {...layer}
            />
          ))}
        </div>
      </div>
    );
  }
}
LayersControl.contextTypes = {
  map: PropTypes.shape({}),
};
LayersControl.defaultProps = {
  style: null,
  disableDemo: false,
  disabled: false,
};
LayersControl.propTypes = {
  layers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
    }),
  ).isRequired,
  style: PropTypes.shape({}),
  disableDemo: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default withMap(LayersControl);
